const Logger = {};

const Config = require("./Config/Config");
const Global = require("./Config/Global");
const ExtLogger = require("./ExtLogger");
const EndpointLogger = require("bq-endpoint-logger");
const Platform = require("./Platform");

const OK = Promise.resolve();

let remoteLoggingEnabled = true;
let endpointLogInProgress = false;
let endpointLogger;
let endpointLoggerContentFields = {
  scheduleId: null,
  presentationId: null,
  placeholderId: null,
  componentId: null,
  scheduleItemUrl: null
};

Logger.disableRemoteLogging = () => {
  remoteLoggingEnabled = false;
};

Logger.initEndpointLogger = (config = {}) => {
  remoteLoggingEnabled = true;
  endpointLogger = EndpointLogger.init(config);
  ExtLogger.setEndpointLoggerFields(config);
};

Logger.setEndpointLoggerContentFields = (contentFields = {}) => {
  endpointLoggerContentFields = Object.assign({}, endpointLoggerContentFields, contentFields);
};

Logger.logDebug = (msg) => {
  if (Config.ENABLE_DEBUG_LOGGING) {
    Logger.log(msg);
  }

  Logger.viewerDebug(msg);
};

Logger.log = (msg, e) => {
  if (window.writeToLog) {
    window.writeToLog(msg);
  }

  /* eslint-disable no-console */
  if (e) {
    console.log(msg, e);
  } else {
    console.log(msg);
  }
};

Logger.logException = (methodName, err, identifier, additionalData) => {
  let eventDetails;
  if (err) {
    eventDetails = err.toString();
  }

  if (identifier) {
    eventDetails = `${eventDetails} - ${identifier}`;
  }

  const eventName = `Exception - ${methodName}`;
  let additionalDataStr;

  if (additionalData) {
    additionalDataStr = JSON.stringify(additionalData);
  }

  const viewerLogMsg = additionalDataStr ? `${eventName} - ${additionalDataStr}` : eventName;

  if (window.writeToLog) {
    window.writeToLog(viewerLogMsg);
  }

  if (additionalData) {
    /* eslint-disable no-console */
    console.log(eventName, err, identifier, additionalData);
  } else {
    console.log(eventName, err, identifier);
  }
  Logger.logExternalMessage(eventName, eventDetails, true);
  Logger.viewerDebug(eventName, eventDetails);
};

Logger.logExternalMessage = (eventName, eventDetails, noConsoleLog) => {
  if (!remoteLoggingEnabled) {
    return;
  }

  ExtLogger.logViewerEvent(eventName, eventDetails, noConsoleLog);
};

const commonLogFields = { // common to viewer heartbeats and viewer log events
  eventApp: "Viewer",
  eventAppVersion: Global.VIEWER_VERSION,
  osVersion: Platform.getOsVersion(),
  browserVersion: Platform.getBrowserNameAndVersion(),
  userAgent: Platform.getUserAgent()
};

const getClientEventAt = () => new Date().toISOString();

const getClientTimezone = () => Platform.getTimezone();

const buildEndpointLogPayload = (eventFields) =>
  Object.assign({}, commonLogFields, endpointLoggerContentFields, eventFields, {
    clientEventAt: getClientEventAt(),
    clientTimezone: getClientTimezone()
  });

const logViewerEvent = (level, eventFields) => {
  if (endpointLogInProgress || !remoteLoggingEnabled || !endpointLogger) {
    return OK;
  }

  const logFn = endpointLogger[level];
  if (!logFn) {
    return OK;
  }

  endpointLogInProgress = true;
  try {
    return logFn(buildEndpointLogPayload(eventFields));
  } finally {
    endpointLogInProgress = false;
  }
};

Logger.recordUptimeHeartbeat = (details = {}) => {
  if (!remoteLoggingEnabled) {
    return;
  }

  endpointLogger.uptimeHeartbeat(Object.assign({}, commonLogFields, endpointLoggerContentFields, details))
  .catch(console.error);
};

Logger.heartbeatInterval = () => {
  return endpointLogger.getHeartbeatIntervalMS();
};

Logger.viewerInfo = (eventDetails, debugInfo) => {
  // avoid console info logging at this level
  return logViewerEvent("info", { eventDetails, debugInfo });
};

Logger.viewerError = (eventErrorCode, eventDetails, debugInfo)  => {
  console.error(eventDetails, debugInfo);
  return logViewerEvent("error", { eventErrorCode, eventDetails, debugInfo });
};

Logger.viewerDebug = (eventDetails, debugInfo) => {
  // prevent console info logging at this level
  return logViewerEvent("debug", { eventDetails, debugInfo });
};

Logger.viewerWarning = (eventDetails, debugInfo) => {
  console.warn(eventDetails, debugInfo);
  return logViewerEvent("warning", { eventDetails, debugInfo });
};

Logger.viewerImportant = (eventDetails, debugInfo) => {
  console.log(eventDetails, debugInfo);

  if (!remoteLoggingEnabled || !endpointLogger) {
    return OK;
  }

  return endpointLogger.important(buildEndpointLogPayload({ eventDetails, debugInfo }));
};

Logger.logTemplateEvent = (eventFields = {}) => {
  if (!eventFields.severity || !eventFields.eventApp || !eventFields.eventAppVersion) {return;}

  if (!endpointLogger) {
    console.warn("logTemplateEvent: endpointLogger not initialized", JSON.stringify(eventFields));
    return;
  }

  const severity = eventFields.severity.toLowerCase();
  const logFn = endpointLogger[severity];
  delete eventFields.severity;

  if (typeof logFn !== "function") {
    console.warn(`Log function for ${severity} not found.`)
    return;
  }

  return logFn(buildEndpointLogPayload(eventFields));
};

Logger.logPlayerEvent = Logger.logTemplateEvent;

Logger.logServiceWorkerEvent = (eventFields = {}) => {
  if (!eventFields.severity || !eventFields.eventDetails) { return; }

  const severity = eventFields.severity.toLowerCase();
  const logFn = endpointLogger[severity];
  delete eventFields.severity;

  if (typeof logFn !== "function") {
    console.warn(`Log function for ${severity} not found.`)
    return;
  }

  return logFn(buildEndpointLogPayload(eventFields));
};

Logger.logAppsEvent = (eventName) => {
  if (!remoteLoggingEnabled) {
    return;
  }

  ExtLogger.logAppsEvent(eventName);
};

Logger.logPlayerConfigData = (config, cb) => {
  if (!remoteLoggingEnabled) {
    return;
  }

  ExtLogger.logPlayerConfigData(config, cb);
};

Logger.logSharedScheduleError = (errorMsg, scheduleItem) => {
  if (!remoteLoggingEnabled) {
    return;
  }

  ExtLogger.logSharedScheduleError(errorMsg, scheduleItem);
};

Logger.reportRemoteDebugToPlayer = (player = null) => {
  if (!player || !player.setRemoteDebugModeOn) {return;}

  return endpointLogger.getLogLevel().then(level => {
    if (level === "DEBUG") { player.setRemoteDebugModeOn(); }
  });
};

const logProofOfPlay = (eventType, correlationId, fields = {}) => {
  const ViewerDataInfo = require("./Data/ViewerDataInfo");

  if (!ViewerDataInfo.isProofOfPlayEnabled()) {
    return OK.then(() => false);
  }

  if (!remoteLoggingEnabled || !endpointLogger || typeof endpointLogger.logProofOfPlay !== "function") {
    return OK.then(() => false);
  }

  const entry = {
    ...fields,
    eventType,
    correlationId,
    recordType: 'schedule-item', // Fixed for now
    clientEventAt: getClientEventAt(),
    clientTimezone: getClientTimezone(),
    scheduleId: endpointLoggerContentFields.scheduleId,
  };

  return endpointLogger.logProofOfPlay(entry)
  .then(() => true)
  .catch(error => {
    console.error("Failed to log proof of play", error);

    return false;
  });
};

const generateCorrelationId = () => {
  return Math.random().toString(36).substring(2, 15) + Math.random().toString(36).substring(2, 15);
};

Logger.logProofOfPlayStart = fields => {
  const correlationId = generateCorrelationId();

  return logProofOfPlay("start", correlationId, fields)
  .then(success => success ? correlationId : null);
};

Logger.logProofOfPlayStop = (correlationId, fields = {}) => {
  return logProofOfPlay("stop", correlationId, fields);
};

window.onerror = (message, source, lineno, colno, error) => {
  const debugInfo = { message, source, lineno, colno, error: error && error.stack };

  let debugInfoString;
  try {
    debugInfoString = JSON.stringify(debugInfo);
  } catch (e) {
    debugInfoString = JSON.stringify({ message, source, lineno, colno, error: "Stack trace unavailable" });
  }

  Logger.viewerError( "E999999999", "Uncaught exception", debugInfoString );
};

module.exports = Logger;
